import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { SectionTitle } from '../title/SectionTitle'
import CardDeleteModal from './CardDeleteModal'
import { getPaymentInfo } from '../../api/pay'
import AFPay from './AFPay/AFPay'

import { MyPayInfo } from '../../@types/cart'
import { PaymentInfoItem } from '../../@types/order'
import { GetOrderSheetsOrderSheetNoResponseWithMyInfo } from './NewOrder'

import { useCommonContext } from '../provider/CommonProvider'

interface PaymentMethodSectionProps {
  orderData: GetOrderSheetsOrderSheetNoResponseWithMyInfo | null
  setSelectPayType: React.Dispatch<React.SetStateAction<string>>
  setCardInfo: React.Dispatch<React.SetStateAction<PaymentInfoItem | null>>
}

declare global {
  interface Window {
    returnMyPay?: (message: string) => void
  }
}

const memberPaymentMethods: string[] = ['신용 / 체크카드', '네이버페이', '카카오페이', '어바웃피싱 페이']
const noMemberPaymentMethods: string[] = ['신용 / 체크카드', '네이버페이', '카카오페이']

const PaymentMethodSection = ({ orderData, setSelectPayType, setCardInfo }: PaymentMethodSectionProps) => {
  const [selectPay, setSelectPay] = useState('신용 / 체크카드')
  const [cardDelete, setCardDelete] = useState<boolean>(false) //카드삭제 확인
  const [changePayData, setChangePayData] = useState<boolean>(false) //마이페이 확인용
  const [deleteModal, setDeleteModal] = useState<boolean>(false)

  const [payUserRegister, setPayUserRegister] = useState<string>('') // 어바웃피싱 페이 유저 확인 코드
  const [myPayData, setMyPayData] = useState<MyPayInfo>() // 마이페이 데이터

  const [cardInfos, setCardInfos] = useState<PaymentInfoItem[]>([]) //어바웃피싱 페이 정보
  const { hasAccessToken, setIsLottie } = useCommonContext()

  const navigate = useNavigate()

  // 결제수단 타입
  const getPayTypeByTitle = (title: string) => {
    switch (title) {
      case '어바웃피싱 페이':
        return 'MY_PAY'
      case '네이버페이':
        return 'NAVER_EASY_PAY'
      case '신용 / 체크카드':
        return 'CREDIT_CARD'
      case '카카오페이':
        return 'KAKAO_PAY'
      default:
        return 'CREDIT_CARD'
    }
  }

  // 결제수단 클릭 시 동기화
  const handlePayClick = (titleName: string) => {
    setSelectPay(titleName)
    setSelectPayType(getPayTypeByTitle(titleName))
  }

  useEffect(() => {
    if (orderData && orderData?.myPayInfo) setMyPayData(orderData.myPayInfo)
  }, [orderData])

  useEffect(() => {
    const fetchPaymentInfo = async () => {
      if (hasAccessToken) {
        try {
          const { data } = await getPaymentInfo()
          setCardInfos(data.paymentInfos)
          setPayUserRegister(data.resultCode)
          setIsLottie(false)
        } catch (err: any) {
          setPayUserRegister(err?.response?.data?.code)
        }
      }
    }
    fetchPaymentInfo()
  }, [cardDelete, changePayData])

  useEffect(() => {
    // 전역 스코프에 returnMypay 함수 정의
    window.returnMyPay = function (paymentInfo: any) {
      if (paymentInfo.date) {
        setChangePayData((prev: any) => !prev)
      }
      if (paymentInfo.url) {
        const result = paymentInfo.url?.split('?')
        navigate(`/orderFinish?${result[1]}&paytype=${paymentInfo.payType}`)
      }
    }
    setSelectPayType(getPayTypeByTitle(selectPay))
  }, [])

  useEffect(() => {
    const handleMessage = (event: any) => {
      if (event.origin !== process.env.REACT_APP_SHOP_URL) return
      if (event.data?.update || event.data?.date) {
        setTimeout(() => {
          setChangePayData((prev) => !prev)
        }, 1000)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // 결제수단 아이템 렌더링
  const renderPayItem = (titleName: string, isMember: boolean) => {
    const isAFPay = titleName === '어바웃피싱 페이'
    const isSelected = selectPay === titleName

    // 결제수단별 아이콘 렌더링
    const renderPayIcon = () => {
      switch (titleName) {
        case '카카오페이':
          return <img src='/images/icon_kakaopay.png' alt='카카오페이 아이콘' className='pay_icon' />
        case '네이버페이':
          return <img src='/images/icon_npay_new.png' alt='네이버페이 아이콘' className='pay_icon' />
        case '어바웃피싱 페이':
          return (
            <img
              src={isSelected ? '/images/icon_afpay_w.png' : '/images/icon_afpay.png'}
              alt='어바웃피싱 페이 아이콘'
              className='pay_icon'
            />
          )
        default:
          return null
      }
    }

    // 신용/체크카드 아이콘 묶음
    const renderCardIcons = () =>
      titleName === '신용 / 체크카드' && (
        <div className='img_box'>
          <img src='/images/icon_card3_toss.png' alt='토스페이 아이콘' />
          <img src='/images/icon_card1_kb.png' alt='kbpay 아이콘' />
          <img src='/images/icon_card2_sh.png' alt='신한페이 아이콘' />
          <img src='/images/icon_card4_lpoint.png' alt='l포인트 아이콘' />
          <img src='/images/icon_card5_apple.png' alt='애플페이 아이콘' />
          <img src='/images/icon_card6_ssg.png' alt='쓱페이 아이콘' />
        </div>
      )

    // 어바웃피싱 페이 컴포넌트
    const renderAFPay = () =>
      isAFPay &&
      isSelected &&
      myPayData && (
        <AFPay
          isMember={isMember}
          cardInfos={cardInfos}
          myPayData={myPayData}
          payUserRegister={payUserRegister}
          setCardDelete={setCardDelete}
          setChangePayData={setChangePayData}
          setCardInfo={setCardInfo}
          setCardInfos={setCardInfos}
          deleteModal={deleteModal}
          setDeleteModal={setDeleteModal}
        />
      )

    return (
      <div key={titleName}>
        <div className='pay_method_cont' onClick={() => handlePayClick(titleName)}>
          <div className={`pay_method_cont_inner ${isSelected ? 'on' : ''}`}>
            {renderPayIcon()}
            <p>{titleName}</p>
            {renderCardIcons()}
            {renderAFPay()}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Section>
        <SectionTitle
          style={{
            padding: '0px',
            display: 'flex',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          <p>결제 수단</p>
        </SectionTitle>
        <div className='pay_method_wrap'>
          {hasAccessToken
            ? memberPaymentMethods.map((titleName) => renderPayItem(titleName, true))
            : noMemberPaymentMethods.map((titleName) => renderPayItem(titleName, false))}
        </div>
      </Section>
      {deleteModal && (
        <CardDeleteModal
          setIsModal={setDeleteModal}
          cardInfos={cardInfos}
          setChangePayData={setChangePayData}
        ></CardDeleteModal>
      )}
    </>
  )
}

const Section = styled.div`
  padding: 24px 20px;
  @media screen and (min-width: 768px) {
    padding: 32px 0 32px 0;
    border-bottom: 1px solid var(--colGray3);
    &:last-child {
      border-bottom: none;
    }
  }
`

export default PaymentMethodSection
